import { toastError, toastSuccess } from '../utils/toastHelper'
import { useQuery, useMutation } from '@tanstack/react-query'
import { ApiClient } from '../services/api'

export interface DetalleEspacioReserva {
  espacio_id: string
  fecha_entrada: string
  fecha_salida: string
  tarifa_unitaria: number
}

export interface ReservaManualRequest {
  cliente_nombre: string
  cliente_email: string
  cliente_telefono?: string
  cliente_documento?: string
  cliente_tipo_documento?: string
  espacio_id: string // Primer espacio (backward compatibility)
  espacios_ids?: string[] // Array de todos los espacios (múltiples)
  fecha_entrada: string
  fecha_salida: string
  cantidad_huespedes: number
  monto_total: number
  monto_pago: number // Monto del primer pago (puede ser parcial)
  metodo_pago?: string
  comprobante_pago?: string
  // ✅ Detalles de espacios - usar DIRECTAMENTE sin recalcular en backend
  espacios_detalle?: DetalleEspacioReserva[]
  crear_dte: boolean
  dte_tipo?: string
  dte_numero?: string
  dte_monto?: number // Monto del DTE (puede ser diferente al pago)
}

export interface ReservaManualResponse {
  reserva_id: string
  reserva_estado: string
  pago_id: number
  pago_estado: string
  dte_id?: string
  dte_numero?: string
  mensaje: string
}

export interface PagoManualRequest {
  reserva_id: string
  monto: number
  metodo_pago: string
  comprobante_pago?: string
  referencia_pago?: string
  metadata?: Record<string, any>
}

export interface DocumentoTributario {
  id: string
  tipo_documento: string
  numero_dte: string
  monto: number
  status_dte: string
  fecha_emision: string
  fecha_vencimiento?: string
  url_documento?: string
}

/**
 * Hook para crear reservas manuales
 */
export const useCrearReservaManual = () => {
  return useMutation({
    mutationFn: async (request: ReservaManualRequest) => {
      // ✅ Nuevo endpoint en /reservas-manuales (no pagos-manuales)
      return await ApiClient.post<ReservaManualResponse>(
        '/api/admin-protected/reservas-manuales/crear',
        request
      )
    },
    onSuccess: (data) => {
      toastSuccess(`Reserva ${data.reserva_id} creada exitosamente`)
    },
    onError: (error: any) => {
      toastError(error.response?.data?.detail || 'Error al crear la reserva')
    },
  })
}

/**
 * Hook para registrar pago manual
 */
export const useRegistrarPagoManual = () => {
  return useMutation({
    mutationFn: async (request: PagoManualRequest) => {
      return await ApiClient.post<any>(
        `/api/admin-protected/pagos-manuales/${request.reserva_id}/registrar`,
        {
          ...request,
          reserva_id: undefined, // No incluir en body
        }
      )
    },
    onSuccess: (data) => {
      toastSuccess('Pago registrado exitosamente')
    },
    onError: (error: any) => {
      toastError(error.response?.data?.detail || 'Error al registrar el pago')
    },
  })
}

/**
 * Hook para obtener DTEs de una reserva
 */
export const useDTEsReserva = (reservaId: string | null) => {
  return useQuery({
    queryKey: ['dtes', reservaId],
    queryFn: async () => {
      if (!reservaId) return []
      return await ApiClient.get<DocumentoTributario[]>(
        `/api/admin-protected/dtes/reserva/${reservaId}`
      )
    },
    enabled: !!reservaId,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Hook para obtener DTEs de un pago específico
 */
export const useDTEsPago = (pagoId: number | null) => {
  return useQuery({
    queryKey: ['dtes-pago', pagoId],
    queryFn: async () => {
      if (!pagoId) return []
      // Obtener DTEs de la reserva y filtrar por pago_id
      const respuesta = await ApiClient.get<DocumentoTributario[]>(
        `/api/admin-protected/dtes?pago_id=${pagoId}`
      )
      return respuesta
    },
    enabled: !!pagoId,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Hook para crear DTE
 */
export const useCrearDTE = () => {
  return useMutation({
    mutationFn: async (request: any) => {
      return await ApiClient.post<DocumentoTributario>(
        '/api/admin-protected/dtes',
        request
      )
    },
    onSuccess: (data) => {
      toastSuccess(`DTE ${data.numero_dte} creado exitosamente`)
    },
    onError: (error: any) => {
      toastError(error.response?.data?.detail || 'Error al crear el DTE')
    },
  })
}

/**
 * Hook para actualizar DTE
 */
export const useActualizarDTE = () => {
  return useMutation({
    mutationFn: async (params: { dteId: string; data: any }) => {
      return await ApiClient.put<DocumentoTributario>(
        `/api/admin-protected/dtes/${params.dteId}`,
        params.data
      )
    },
    onSuccess: (data) => {
      toastSuccess(`DTE ${data.numero_dte} actualizado`)
    },
    onError: (error: any) => {
      toastError(error.response?.data?.detail || 'Error al actualizar el DTE')
    },
  })
}

/**
 * Hook para anular DTE
 */
export const useAnularDTE = () => {
  return useMutation({
    mutationFn: async (dteId: string) => {
      return await ApiClient.delete(`/api/admin-protected/dtes/${dteId}`)
    },
    onSuccess: () => {
      toastSuccess('DTE anulado exitosamente')
    },
    onError: (error: any) => {
      toastError(error.response?.data?.detail || 'Error al anular el DTE')
    },
  })
}

/**
 * Hook para obtener tipos de espacios
 */
export const useTiposEspacios = () => {
  return useQuery({
    queryKey: ['tipos-espacios'],
    queryFn: async () => {
      return await ApiClient.get<any[]>(
        '/api/admin-protected/tipos-espacios'
      )
    },
    staleTime: 10 * 60 * 1000, // 10 minutos
  })
}

/**
 * Hook para obtener espacios por tipo
 */
export const useEspaciosPorTipo = (tipoEspacioId: string | null) => {
  return useQuery({
    queryKey: ['espacios', tipoEspacioId],
    queryFn: async () => {
      if (!tipoEspacioId) return []
      return await ApiClient.get<any[]>(
        `/api/admin-protected/espacios?tipo_espacio_id=${tipoEspacioId}`
      )
    },
    enabled: !!tipoEspacioId,
    staleTime: 5 * 60 * 1000, // 5 minutos
  })
}

